import { convolveReal, fftReal, nextPow2 } from './fft.ts';
import { applyFades, EPS } from './util.ts';

/**
 * Exponential ("logarithmic") sine sweep and its matching inverse filter,
 * after Farina 2000.
 *
 * The sweep sweeps *upward* on purpose. Loudspeaker distortion products in an
 * up-sweep deconvolve to *negative* time - they land ahead of the linear
 * impulse response, where they can be windowed off. A down-sweep smears them
 * straight into the reverb tail you are trying to measure.
 */
export interface SweepSpec {
  sampleRate: number;
  /**
   * Start frequency in Hz. Two things fight here: phone speakers produce
   * nothing useful below ~300 Hz, and the low band edge sets how sharp the
   * deconvolved impulse is - it rings for roughly one period of `startHz`,
   * so 200 Hz smears the first ~5 ms (~0.9 m) of the range profile. Range
   * measurement therefore band-passes the IR upward before enveloping it.
   */
  startHz: number;
  /** End frequency in Hz. Kept below Nyquist with headroom for the fade. */
  endHz: number;
  durationSec: number;
  /**
   * Fade lengths. Longer than click suppression needs, because a fade in time
   * is a spectral taper at the band edge and a hard band edge rings as a sinc
   * - false echoes either side of the direct sound. The fades cost usable
   * bandwidth though (see `usableLowHz`), so they only do part of the job;
   * the rest is the smooth band-limit applied to the impulse response during
   * analysis.
   */
  fadeInSec: number;
  fadeOutSec: number;
  /** Peak amplitude of the played signal, 0..1. */
  amplitude: number;
}

export interface Sweep {
  spec: SweepSpec;
  /** The signal to play, faded and scaled to `spec.amplitude`. */
  samples: Float32Array;
  /**
   * Time-reversed, amplitude-corrected sweep. Convolving a recording with this
   * yields the impulse response. Scaled so a unity system gives a unit impulse.
   */
  inverse: Float64Array;
  /** Index in `convolveReal(samples, inverse)` where the impulse lands. */
  impulseIndex: number;
  /** Sweep rate in octaves per second - the whitening slope of the inverse. */
  octavesPerSecond: number;
  /**
   * Band over which the sweep actually delivers full energy, i.e. excluding
   * the frequencies swallowed by the fades. Deconvolution divides by the
   * sweep's own spectrum, so outside this band it is dividing by almost
   * nothing and amplifying whatever noise happens to be there. Analysis
   * band-limits to this range.
   */
  usableLowHz: number;
  usableHighHz: number;
  /**
   * How far ahead of the impulse response the second-harmonic product lands.
   *
   * Distortion products arrive early, but each one drags its own reverb tail
   * forward from there. They are only truly separated from the measurement if
   * this exceeds the room's RT60 - which sets a floor on sweep duration:
   *
   *   duration > RT60 * log2(endHz / startHz)
   *
   * A 1.2 s sweep over 6.3 octaves separates cleanly up to RT60 = 0.19 s.
   * Past that, an overdriven speaker starts feeding its own distortion into
   * the decay it is supposed to be measuring. Long rooms want long sweeps.
   */
  harmonicSeparationSec: number;
}

export const SWEEP_PRESETS = {
  /** Default. Long enough for a clean tail on a phone speaker in a real room. */
  room: { startHz: 200, endHz: 16000, durationSec: 1.2, fadeInSec: 0.05, fadeOutSec: 0.03, amplitude: 0.85 },
  /** Fast repeat rate for the live range profile; worse SNR, worse RT60. */
  quick: { startHz: 500, endHz: 15000, durationSec: 0.35, fadeInSec: 0.02, fadeOutSec: 0.01, amplitude: 0.85 },
  /**
   * Mapping. Dozens of these are fired back to back while the phone turns, so
   * the trade is different from a single measurement: what is wanted is many
   * looks from many angles, not one immaculate impulse response.
   *
   * Short, because the scan has to finish inside one comfortable turn and the
   * phone must not have swung far during any single chirp - a quarter second
   * at a typical turning rate smears each look over about five degrees, which
   * is finer than the handset's own directivity and therefore free. The
   * processing gain drops to about 35 dB from the 43 dB of the default, and
   * that is paid back several times over by integrating fifty looks.
   *
   * Starting high, because mapping does not want the bottom of the band at
   * all. Range resolution is set by bandwidth and the phone's directivity only
   * exists where its body is acoustically large, so both argue for the top of
   * the spectrum; below about a kilohertz a handset radiates almost nothing
   * and a deconvolved impulse rings for a period of the low edge, smearing the
   * first metre of every profile.
   */
  scan: { startHz: 900, endHz: 16000, durationSec: 0.25, fadeInSec: 0.02, fadeOutSec: 0.012, amplitude: 0.9 },
  /** Big/quiet spaces: more processing gain, longer decay window. */
  hall: { startHz: 120, endHz: 16000, durationSec: 3.0, fadeInSec: 0.1, fadeOutSec: 0.05, amplitude: 0.9 },
} as const satisfies Record<string, Omit<SweepSpec, 'sampleRate'>>;

export type SweepPresetName = keyof typeof SWEEP_PRESETS;

export function sweepSpec(sampleRate: number, preset: SweepPresetName = 'room'): SweepSpec {
  const base = SWEEP_PRESETS[preset];
  const nyquist = sampleRate / 2;
  return {
    sampleRate,
    startHz: base.startHz,
    // Leave a little room below Nyquist; the very top of the band is worthless
    // on a phone speaker anyway.
    endHz: Math.min(base.endHz, nyquist * 0.92),
    durationSec: base.durationSec,
    fadeInSec: base.fadeInSec,
    fadeOutSec: base.fadeOutSec,
    amplitude: base.amplitude,
  };
}

/** Instantaneous frequency of the sweep at time t, in Hz. */
export function instantaneousFrequency(spec: SweepSpec, tSec: number): number {
  const r = Math.log(spec.endHz / spec.startHz);
  return spec.startHz * Math.exp((tSec / spec.durationSec) * r);
}

export function generateSweep(spec: SweepSpec): Sweep {
  validate(spec);

  const n = Math.max(2, Math.round(spec.durationSec * spec.sampleRate));
  const t = spec.durationSec;
  const w1 = 2 * Math.PI * spec.startHz;
  const r = Math.log(spec.endHz / spec.startHz);
  // phase(t) = (w1*T/R) * (e^(t*R/T) - 1)
  const k = (w1 * t) / r;

  const raw = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const tt = i / spec.sampleRate;
    raw[i] = Math.sin(k * (Math.exp((tt * r) / t) - 1));
  }

  applyFades(raw, spec.fadeInSec * spec.sampleRate, spec.fadeOutSec * spec.sampleRate);

  // The inverse filter is the played sweep reversed in time, shaded by an
  // envelope that decays 6 dB per octave *along the reversed signal*.
  //
  // The index that envelope runs on is the whole game. A log sweep has a pink
  // magnitude spectrum, |S(f)| proportional to f^-1/2, so a flat deconvolution
  // needs |Inv(f)| proportional to f^+1/2. Because a sweep plays one frequency
  // at a time, a time envelope *is* a spectral shaping - and in the reversed
  // sweep time runs from high frequency to low, so a decaying envelope
  // attenuates the low end and gives exactly that f^+1/2 tilt. Run the same
  // envelope on the original sweep's index instead and it tilts the other way:
  // the deconvolved "impulse" comes out shaped like 1/f^2, a low-frequency
  // blob that drifts hundreds of samples away from the true arrival and takes
  // every echo distance with it.
  const inverse = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    inverse[i] = raw[n - 1 - i] * Math.exp((-i / (n - 1)) * r);
  }

  const samples = new Float32Array(n);
  for (let i = 0; i < n; i++) samples[i] = raw[i] * spec.amplitude;

  const octavesPerSecond = r / Math.LN2 / spec.durationSec;
  const usableLowHz = spec.startHz * Math.pow(2, octavesPerSecond * spec.fadeInSec);
  const usableHighHz = spec.endHz / Math.pow(2, octavesPerSecond * spec.fadeOutSec);
  const harmonicSeparationSec = (spec.durationSec * Math.LN2) / r;

  // Calibrate against the signal actually played, so a unity acoustic path
  // deconvolves to a unity impulse response whatever the playback level.
  //
  // Normalising the *passband gain* rather than the impulse's peak sample is
  // the difference between a correct impulse response and one that is 3.6 dB
  // wrong. A band-limited impulse of unit height does not have unit gain: its
  // peak is (2 * bandwidth / sampleRate) times its passband gain, so scaling
  // the time-domain peak to 1 leaves everything genuinely band-limited - the
  // whole reverb tail - inflated by 1/0.66. Wrong direct-to-reverberant ratio,
  // wrong absolute level in an exported IR.
  const gain = 1 / passbandGain(samples, inverse, spec, usableLowHz, usableHighHz);
  for (let i = 0; i < n; i++) inverse[i] *= gain;

  return {
    spec,
    samples,
    inverse,
    impulseIndex: n - 1,
    octavesPerSecond,
    usableLowHz,
    usableHighHz,
    harmonicSeparationSec,
  };
}

/**
 * Median magnitude response of sweep-convolved-with-inverse across the usable
 * band. Median rather than mean so a single resonant bin cannot drag it.
 */
function passbandGain(
  samples: Float32Array,
  inverse: Float64Array,
  spec: SweepSpec,
  lowHz: number,
  highHz: number,
): number {
  const probe = convolveReal(samples, inverse);
  const size = nextPow2(probe.length);
  const { re, im } = fftReal(probe, size);
  const binHz = spec.sampleRate / size;
  const lo = Math.max(1, Math.ceil(lowHz / binHz));
  const hi = Math.min(size >> 1, Math.floor(highHz / binHz));
  if (hi <= lo) return 1;

  const magnitudes = new Float64Array(hi - lo);
  for (let k = lo; k < hi; k++) magnitudes[k - lo] = Math.hypot(re[k], im[k]);
  magnitudes.sort();
  const median = magnitudes[magnitudes.length >> 1];
  return median > EPS ? median : 1;
}

function validate(spec: SweepSpec): void {
  const nyquist = spec.sampleRate / 2;
  if (!(spec.sampleRate > 0)) throw new Error('sweep: sampleRate must be positive');
  if (!(spec.startHz > 0)) throw new Error('sweep: startHz must be positive');
  if (!(spec.endHz > spec.startHz)) throw new Error('sweep: endHz must exceed startHz');
  if (spec.endHz > nyquist) throw new Error(`sweep: endHz ${spec.endHz} exceeds Nyquist ${nyquist}`);
  if (!(spec.durationSec > 0)) throw new Error('sweep: durationSec must be positive');
  if (spec.fadeInSec + spec.fadeOutSec >= spec.durationSec) {
    throw new Error('sweep: fades are longer than the sweep');
  }
}
